import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { useReaderMode } from '../../hooks/useReaderMode';
import { useReaderTts, useTtsShared } from '../../hooks/useTtsContext';
import { useComposerStore } from '../../state/composerStore';
import { useDocTtsStore } from '../../state/docTtsStore';
import {
  anchorAtOffset,
  anchorToScreenY,
  screenYToAnchor,
  textFromAnchor,
} from '../../utils/textWalker';
import { tokenize, speakableText } from '../../utils/sentenceTokenizer';

const READER_HINT_KEY = 'va-reader-hinted';
const READER_HINT_COUNT_KEY = 'va-reader-hint-count';

// Drag-as-scroll: while dragging, when the pointer enters the top/bottom 15%
// of the scroll container, scroll at a rate proportional to depth into the zone.
const EDGE_ZONE_PCT = 0.15;
const MAX_SCROLL_PX_PER_FRAME = 18;

function findScrollableAncestor(el: HTMLElement | null): HTMLElement | Window {
  let node: HTMLElement | null = el;
  while (node) {
    const cs = window.getComputedStyle(node);
    const overflowY = cs.overflowY;
    const scrollable = (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay')
      && node.scrollHeight > node.clientHeight + 1;
    if (scrollable) return node;
    node = node.parentElement;
  }
  return window;
}

function getScrollRect(target: HTMLElement | Window): { top: number; height: number } {
  if (target === window) {
    return { top: 0, height: window.innerHeight };
  }
  const rect = (target as HTMLElement).getBoundingClientRect();
  return { top: rect.top, height: rect.height };
}

function scrollBy(target: HTMLElement | Window, dy: number) {
  if (target === window) {
    window.scrollBy(0, dy);
  } else {
    (target as HTMLElement).scrollTop += dy;
  }
}

export function ReaderOverlay() {
  const reader = useReaderMode();
  const tts = useReaderTts();
  const sharedTts = useTtsShared();
  // docTtsStore is the third TTS channel (doc viewer + chat-message read-aloud).
  // ReaderOverlay is always mounted (main.tsx), so it's the host for the
  // docTtsStore↔(shared + reader) cross-stop direction — keeping the 3-way mutual
  // exclusion out of ChatArea. True/false = a doc/chat read is audibly active.
  const docTtsActive = useDocTtsStore((s) => s.phase !== 'idle' && s.phase !== 'stopped');
  const [lineY, setLineY] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);
  const containerRef = useRef<HTMLElement | null>(null);
  const [showHint, setShowHint] = useState(false);
  const hintTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Capture the anchor at the moment reading starts so sentence offsets
  // are stable even if the drag handle moves mid-playback.
  const readStartAnchorRef = useRef(reader.anchor);

  // Drag-as-scroll state
  const dragPointerYRef = useRef<number | null>(null);
  const scrollTargetRef = useRef<HTMLElement | Window | null>(null);
  const scrollRafRef = useRef<number | null>(null);

  const { active, activeSurfaceId, anchor, getSurface, setAnchor } = reader;

  const dismissHint = useCallback(() => {
    setShowHint(false);
    if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
    try { localStorage.setItem(READER_HINT_KEY, '1'); } catch {}
  }, []);

  // Show first-time hint on 3rd activation.
  useEffect(() => {
    if (!active) return;
    try {
      if (localStorage.getItem(READER_HINT_KEY)) return;
      const count = parseInt(localStorage.getItem(READER_HINT_COUNT_KEY) ?? '0', 10) + 1;
      localStorage.setItem(READER_HINT_COUNT_KEY, String(count));
      if (count >= 3) {
        setShowHint(true);
        hintTimerRef.current = setTimeout(dismissHint, 8000);
      }
    } catch {}
    return () => { if (hintTimerRef.current) clearTimeout(hintTimerRef.current); };
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track the active surface's container.
  useEffect(() => {
    if (!active || !activeSurfaceId) {
      containerRef.current = null;
      return;
    }
    containerRef.current = getSurface(activeSurfaceId);
  }, [active, activeSurfaceId, getSurface]);

  // Initialize the line at viewport center the first time Reader Mode opens.
  useEffect(() => {
    if (!active) { setLineY(null); return; }
    if (anchor) return;

    const container = containerRef.current;
    if (!container) return;

    const initialY = window.innerHeight / 2;
    const a = screenYToAnchor(container, activeSurfaceId || '', initialY);
    if (a) {
      setAnchor(a);
      setLineY(initialY);
    } else {
      setLineY(initialY);
    }
  }, [active, activeSurfaceId, anchor, setAnchor]);

  // When anchor changes (drag, sentence advance), recompute screen Y.
  useEffect(() => {
    if (!reader.active || !reader.anchor) return;
    const container = containerRef.current;
    if (!container) return;

    const update = () => {
      const y = anchorToScreenY(container, reader.anchor!);
      if (y != null) setLineY(y);
    };
    update();

    window.addEventListener('scroll', update, { passive: true, capture: true });
    window.addEventListener('resize', update, { passive: true });
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [reader.active, reader.anchor]);

  // Stop TTS when Reader Mode is deactivated (× button or toggle).
  useEffect(() => {
    if (!active && reader.playState === 'playing') {
      tts.stop();
    }
  }, [active, reader.playState, tts]);

  // Sync playState back to idle when TTS finishes naturally.
  useEffect(() => {
    if (reader.playState === 'playing' && !tts.speaking) {
      reader.setPlayState('idle');
    }
  }, [tts.speaking, reader]);

  // Cross-stop: if shared TTS starts (per-message replay or streaming auto-read)
  // while Reader is reading, stop Reader so the two channels never overlap.
  useEffect(() => {
    if (sharedTts.speaking && reader.playState === 'playing') {
      tts.stop();
      reader.setPlayState('idle');
    }
  }, [sharedTts.speaking, reader.playState, tts, reader]);

  // 3-way cross-stop (docTtsStore = doc viewer + chat-message read-aloud) — with
  // READ-ALOUD PRIORITY over the shared pill / streaming auto-read:
  //
  // (1) docTtsStore read becomes active → stop the shared pill/streaming TTS AND
  //     Reader so only the docTtsStore channel speaks. This is the reverse of the
  //     start-side stops (ChatArea.onReadMessageAloud calls sharedTts.stop();
  //     CodeViewer rides the same store) — it covers the case where docTtsStore is
  //     started while shared/reader are already speaking. Read-aloud wins on its
  //     explicit start. Rising-edge guard (`docTtsActive` dep only) fires once when
  //     the read begins.
  useEffect(() => {
    if (!docTtsActive) return;
    if (sharedTts.speaking) sharedTts.stop();
    if (reader.playState === 'playing') {
      tts.stop();
      reader.setPlayState('idle');
    }
  }, [docTtsActive]); // eslint-disable-line react-hooks/exhaustive-deps

  // NOTE: the former watcher (2) "shared TTS starts → stop the active docTtsStore
  // read" has been REMOVED. Read-aloud now has PRIORITY: while a docTtsStore read
  // is active, the shared channel must NOT start at all (gated at the source in
  // useTTS.speak() + useTTS.feedChunk via isDocTtsReadActive(); P4 #1652 retired the
  // play-latest pill, so there is no longer a button to disable), so there is
  // nothing for it to stop here — and removing
  // the watcher eliminates the instant-cancel race where a new agent turn's
  // streaming auto-read killed an in-progress read-aloud. The no-double-audio
  // invariant still holds because "read-aloud active ⇒ shared suppressed".

  // Self-echo prevention: stop Reader narration when the mic goes hot, so the live
  // mic doesn't capture it. (App.tsx handles chat + document TTS; Reader owns its
  // own TTS instance, so it must stop itself.)
  const micRecordingSessionId = useComposerStore((s) => s.globalRecording.sessionId);
  useEffect(() => {
    if (micRecordingSessionId && reader.playState === 'playing') {
      tts.stop();
      reader.setPlayState('idle');
    }
  }, [micRecordingSessionId, reader.playState, tts, reader]);

  // Drag-as-scroll RAF loop. Runs while pointer is in top/bottom edge zone.
  const stopScrollLoop = useCallback(() => {
    if (scrollRafRef.current != null) {
      cancelAnimationFrame(scrollRafRef.current);
      scrollRafRef.current = null;
    }
    scrollTargetRef.current = null;
  }, []);

  const tickScroll = useCallback(() => {
    const target = scrollTargetRef.current;
    const py = dragPointerYRef.current;
    if (target == null || py == null) {
      scrollRafRef.current = null;
      return;
    }
    const { top, height } = getScrollRect(target);
    const relY = py - top;
    const edge = height * EDGE_ZONE_PCT;
    let dy = 0;
    if (relY < edge) {
      const depth = (edge - relY) / edge; // 0..1
      dy = -Math.ceil(depth * MAX_SCROLL_PX_PER_FRAME);
    } else if (relY > height - edge) {
      const depth = (relY - (height - edge)) / edge;
      dy = Math.ceil(depth * MAX_SCROLL_PX_PER_FRAME);
    }
    if (dy !== 0) {
      scrollBy(target, dy);
      // After scrolling, recompute anchor under the line and let the scroll
      // handler reposition the overlay naturally.
      const container = containerRef.current;
      if (container) {
        const a = screenYToAnchor(container, reader.activeSurfaceId || '', py);
        if (a) reader.setAnchor(a);
      }
    }
    scrollRafRef.current = requestAnimationFrame(tickScroll);
  }, [reader]);

  // Drag handlers.
  const onPointerDown = useCallback((e: PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    setDragging(true);
    scrollTargetRef.current = findScrollableAncestor(containerRef.current);
    dragPointerYRef.current = e.clientY;
  }, []);

  const onPointerMove = useCallback((e: PointerEvent) => {
    if (!dragging) return;
    e.preventDefault();
    setLineY(e.clientY);
    dragPointerYRef.current = e.clientY;

    const container = containerRef.current;
    if (!container) return;
    const a = screenYToAnchor(container, reader.activeSurfaceId || '', e.clientY);
    if (a) reader.setAnchor(a);

    // Trigger drag-as-scroll if pointer is in edge zone.
    const target = scrollTargetRef.current;
    if (!target) return;
    const { top, height } = getScrollRect(target);
    const relY = e.clientY - top;
    const edge = height * EDGE_ZONE_PCT;
    const inEdge = relY < edge || relY > height - edge;
    if (inEdge && scrollRafRef.current == null) {
      scrollRafRef.current = requestAnimationFrame(tickScroll);
    }
  }, [dragging, reader, tickScroll]);

  const onPointerUp = useCallback((e: PointerEvent) => {
    if (!dragging) return;
    setDragging(false);
    (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
    dragPointerYRef.current = null;
    stopScrollLoop();
  }, [dragging, stopScrollLoop]);

  // Defensive: stop any running scroll loop on unmount.
  useEffect(() => stopScrollLoop, [stopScrollLoop]);

  // Tap the book icon → start or stop reading.
  const onBookTap = useCallback(() => {
    if (dragging) return;
    const container = containerRef.current;
    if (!container || !reader.anchor) return;

    if (reader.playState === 'playing') {
      // Stop reading
      tts.stop();
      reader.setPlayState('idle');
      return;
    }

    // Cross-stop: silence any in-flight per-message TTS AND any docTtsStore read
    // (doc viewer / chat-message read-aloud) so only Reader speaks (P2, 3-way).
    sharedTts.stop();
    useDocTtsStore.getState().stop();

    // Start reading from current anchor
    const startAnchor = reader.anchor;
    readStartAnchorRef.current = startAnchor;
    const text = textFromAnchor(container, startAnchor);
    const tokens = tokenize(text);
    if (tokens.length === 0) return;

    const sentenceTexts = tokens.map(speakableText);
    const sentenceOffsets = tokens.map((t) => t.offset);

    reader.setPlayState('playing');
    tts.speakSentences(sentenceTexts, (idx) => {
      const offset = sentenceOffsets[idx] ?? 0;
      if (offset > 0 && readStartAnchorRef.current) {
        const newAnchor = anchorAtOffset(container, readStartAnchorRef.current, offset);
        if (newAnchor) reader.setAnchor(newAnchor);
      }
    });
  }, [dragging, reader, tts, sharedTts]);

  if (!reader.active || lineY == null) return null;

  const playing = reader.playState === 'playing';

  return (
    <>
      <div
        class="reader-overlay"
        style={{ top: `${lineY}px` }}
        role="region"
        aria-label="Reader cursor"
      >
        <div class="reader-line" aria-hidden="true" />
        <button
          type="button"
          class={`reader-book${dragging ? ' dragging' : ''}${playing ? ' playing' : ''}`}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onClick={onBookTap}
          aria-label={playing ? 'Stop reading' : 'Start reading from this position'}
          title={playing ? 'Tap to stop' : 'Drag to move · tap to read'}
        >
          <span class="reader-book-icon" aria-hidden="true">
            {playing ? '⏸' : '📖'}
          </span>
        </button>
        <button
          type="button"
          class="reader-close"
          onClick={reader.deactivate}
          aria-label="Exit Reader Mode"
          title="Exit Reader Mode"
        >
          ×
        </button>
      </div>
      {showHint && (
        <div class="reader-hint-toast" role="status" aria-live="polite">
          Drag 📖 to position · tap to read aloud
          <button class="reader-hint-toast-close" onClick={dismissHint} aria-label="Dismiss">×</button>
        </div>
      )}
    </>
  );
}
